/**
 * Help section copy (French). Same keys as helpSections.en.ts
 */
import type { HelpSectionMap } from './helpSections.en';

const helpSectionsFr: HelpSectionMap = {
  'getting-started': {
    intro:
      'Voici l’essentiel pour démarrer. Suivez ces étapes ou explorez la barre latérale.',
    stepsTitle: 'Vos premiers pas',
    step1: 'Ouvrez **Vue d’ensemble** pour le tableau de bord et l’activité récente.',
    step2: 'Ouvrez **Mes tâches** pour tous les tickets qui vous sont assignés.',
    step3: 'Cliquez sur un **Projet** dans la barre latérale pour le tableau, backlog, feuille de route, etc.',
    stepPm:
      'Ouvrez **Gestion de projet** pour tous les projets et une vue feuille de route (étape actuelle, prochain déploiement, date prévue).',
    step4: 'Utilisez **Créer** dans l’en-tête pour ajouter un ticket.',
    step5:
      'Utilisez le **commutateur de thème** (soleil/lune) pour le mode clair ou sombre.',
    outro:
      'Utilisez les liens à gauche pour accéder à un sujet, ou la visite rapide ci-dessus.',
  },
  notifications: {
    intro:
      'L’icône cloche affiche les notifications in-app. Cliquez pour ouvrir le centre et marquer comme lues.',
    whatTitle: 'Objet des notifications',
    n1: 'Quand on vous assigne un ticket',
    n2: 'Quand vous êtes **@mentionné** dans un commentaire',
    n3: 'Changements de statut, commentaires et rappels d’échéance',
    n4: 'Invitations de projet (si votre organisation les utilise)',
    prefsTitle: 'Préférences',
    prefsBody:
      'Dans le profil ou les paramètres du compte, choisissez les notifications par e-mail et in-app (p. ex. assignations seulement ou aussi mentions).',
    sessionsBody:
      'Depuis votre profil, consultez aussi **Sessions actives** et déconnectez d’autres appareils.',
    tip: 'Ouvrez la cloche et cliquez sur « Tout marquer comme lu » pour vider la liste.',
  },
  'keyboard-shortcuts': {
    intro:
      'À utiliser lorsque le contenu principal est focalisé. Sur Mac **Cmd**, sur Windows/Linux **Ctrl**.',
    k1: '**Cmd/Ctrl + K** ou **/** – Rechercher tickets et projets (spotlight). Désactivé si un modal ticket est ouvert.',
    k2: '**Cmd/Ctrl + I** – Ouvrir Créer un ticket',
    k3: '**Cmd/Ctrl + Enter** – Envoyer le formulaire de création',
    k4: '**Cmd/Ctrl + S** – Enregistrer (dans l’éditeur de doc)',
    k5: '**Échap** – Fermer un modal ou la recherche',
    tip: 'Appuyez sur / ou Cmd/Ctrl + K pour chercher sans quitter la page. La vue liste utilise le défilement virtualisé pour les grandes listes.',
  },
  projects: {
    intro:
      'Les projets regroupent tickets, sprints et rapports. Ouvrez tout projet dont vous êtes membre depuis la barre latérale.',
    createTitle: 'Créer un projet',
    createBody:
      'Allez dans **Gestion de projet** et cliquez **Créer un projet**. Nom, description, catégorie. Vous pouvez utiliser un modèle pour préremplir workflows et paramètres.',
    membersTitle: 'Membres',
    membersLead:
      'Ouvrez **Gestion de projet** pour la liste complète, l’export CSV et la **Vue feuille de route** (étape, prochain déploiement, date par projet). Ouvrez un projet puis **Gérer les membres** pour :',
    m1: 'Ajouter des personnes et définir le rôle (Admin, Membre ou Lecteur).',
    m2:
      'Ajouter un **groupe** (Paramètres admin → Groupes) pour le même rôle pour tous.',
    m3: 'Modifier un rôle ou retirer quelqu’un.',
    rolesHint:
      'Lecteur = lecture seule. Membre = modifier les tickets et commenter. Admin = membres et paramètres. Au moins un admin projet requis.',
    notePlan:
      'La création peut être limitée par le plan (Free, Pro, Enterprise). Voir Offres et limites ci-dessous.',
    memberOnly:
      'Seuls les admins et chefs de projet peuvent créer des projets. Demandez à votre admin ou utilisez la demande d’accès.',
  },
  issues: {
    intro:
      'Les tickets sont les éléments suivis : tâches, bugs, stories, epics et sous-tâches.',
    createTitle: 'Créer un ticket',
    c1: 'Cliquez **Créer** dans l’en-tête.',
    c2: 'Choisissez **Créer un ticket** ou **Création groupée depuis CSV**.',
    c3: 'Remplissez le formulaire (projet, titre, type, échéance, etc.) et validez.',
    createTip: 'Vous pouvez aussi créer depuis le tableau ou le backlog.',
    typesTitle: 'Types de tickets',
    typeTask: '**Tâche** – Travail général',
    typeStory: '**Story** – Fonctionnalité utilisateur',
    typeBug: '**Bug** – Correctif',
    typeEpic: '**Epic** – Grande initiative ; autres tickets peuvent lier',
    typeSub: '**Sous-tâche** – Enfant d’un autre ticket',
    fieldsTitle: 'Ce que vous pouvez définir',
    fieldsBody:
      'Titre, description, assigné, priorité, statut et échéance sont la base. Ajoutez libellés, composants, versions, lien parent ou epic, estimations et pièces jointes. À la création vous pouvez ajouter un **ticket lié** et le type de lien (ex. Bloque, Est bloqué par, Lié à, Dupliqué) ; le type est mémorisé pour le prochain lien. Ouvrez un ticket pour tout modifier et commenter.',
  },
  backlog: {
    intro:
      'Tous les tickets hors sprint et gestion des sprints. Glissez pour réordonner le backlog ou vers un sprint.',
    b1: 'Glissez des tickets dans un sprint pour planifier.',
    b2: 'Créez des sprints et passez-les de planifié → actif → terminé.',
    b3: 'Remettez des tickets au backlog si besoin.',
    b4:
      'Les gros backlogs utilisent le défilement virtualisé ; le glisser-déposer conserve le même ordre.',
  },
  'project-management': {
    intro:
      'Pour les admins et chefs de projet. Ouvrez **Gestion de projet** pour la liste des projets et la feuille de route de haut niveau.',
    allTitle: 'Tous les projets',
    allBody:
      'Le tableau liste chaque projet. Création, export CSV, ouverture tableau, backlog, feuille de route ou docs. **Gérer les membres** par projet.',
    archivedBody:
      'Activez **Afficher les archivés** pour inclure les projets archivés. Les admins org et projet archivent ou restaurent via **Actions** — les archivés sont masqués des listes par défaut et de la plupart des sélecteurs jusqu’à restauration.',
    roadmapTitle: 'Vue feuille de route',
    roadmapLead:
      'En haut, la **Vue feuille de route** affiche, pour chaque projet :',
    ro1: '**Étape actuelle** – Dernière version publiée (le cas échéant).',
    ro2: '**Prochain déploiement** – Nom de la prochaine version planifiée.',
    ro3: '**Date prévue** – Date de publication de cette version si définie.',
    roadmapFoot:
      'Voyez où en est chaque projet et la prochaine livraison. Cliquez sur le nom ou **Voir la feuille de route complète** pour la vue multi-projets ou celle du projet.',
    roadmapNote:
      'Les versions se configurent par projet (paramètres admin ou projet). Ajoutez des versions et **Version de correction** sur les tickets pour alimenter la feuille de route.',
  },
  roadmap: {
    intro:
      'Les feuilles de route montrent versions et livraisons. FlowBoard propose une feuille de route par projet et une multi-projets pour l’organisation.',
    projTitle: 'Feuille de route projet',
    projLead: 'Ouvrez un projet et choisissez **Feuille de route** dans la barre latérale. Vous verrez :',
    pr1:
      'Une carte avec **Étape actuelle**, **Prochain déploiement** et **Date prévue**.',
    pr2:
      '**Vue liste** – Chronologie des versions avec descriptions, dates de début/publication et tickets (Version de correction / Version affectée).',
    pr3:
      '**Vue chronologique** – Versions en barres ; glissez les badges de tickets entre versions pour changer la **Version de correction**.',
    fixBody:
      'Ajoutez des versions dans le projet (ou l’org) et définissez **Version de correction** / Version affectée sur les tickets.',
    crossTitle: 'Feuille de route multi-projets',
    crossBody:
      'Dans la barre latérale, **Feuille de route multi-projets** liste toutes les versions de tous les projets. Chaque ligne : projet, version, date, compteurs (Correction / Affecte). Cliquez sur un projet pour sa feuille de route.',
    tip: 'Servez-vous de la feuille de route pour aligner les parties prenantes. Définissez dates de début et de publication sur les versions pour des chronologies précises.',
  },
  calendar: {
    body: 'Les tickets avec échéance apparaissent sur le calendrier. Changez de mois pour planifier ou repérer le retard.',
  },
  'list-view': {
    intro:
      'Tableau de tous les tickets du projet. Triez, filtrez et modifiez en masse statut ou assigné.',
    lv1: 'Filtres avancés par statut, assigné, libellé, etc.',
    lv2: 'Sélectionnez plusieurs tickets pour changer statut, assigné ou supprimer en une fois.',
    lvBulk:
      'La modification groupée met à jour beaucoup de tickets. Depuis une liste projet vous pouvez aussi définir libellés, composants, versions de correction et résolution.',
    lv3:
      'Pour les grandes listes, utilisez la **pagination** en bas pour changer de page et la taille de page.',
    lv4:
      '**Mise en page enregistrée** stocke filtre, recherche, tri et colonnes dans ce navigateur (séparé des filtres enregistrés côté serveur). Activez la colonne **SLA** pour les dépassements actifs.',
    tip: 'La vue liste convient pour comparer beaucoup de tickets ou les mises à jour de masse.',
  },
  'dependency-graph': {
    body: 'Carte visuelle des liens entre tickets (bloque, bloqué par, lié). Utile pour l’impact et les chemins critiques.',
  },
  docs: {
    intro:
      'Chaque projet a une zone **Docs** (type Confluence) pour specs et notes. Ouvrez un projet puis **Docs** dans la barre latérale.',
    tplTitle: 'Modèles de page et libellés',
    tplBody:
      'À la création ou l’édition, choisissez un **modèle** (ex. Notes de réunion, Spec) et des **libellés** pour organiser et retrouver vite.',
    sidebarTitle: 'Barre latérale gauche',
    sidebarBody:
      'L’arborescence des pages à gauche. **Nouvelle page** pour créer ; recherche par titre. Développez ou repliez ; cliquez un titre pour ouvrir.',
    pageTitle: 'Sur la page',
    d1: '**Sur cette page** – Sommaire fixe à partir des titres H2/H3 en consultation.',
    d2: '**Pages enfants** – Sous-pages listées sous le contenu avec liens.',
    d3: '**Modifier** – Titre, parent, contenu. Barre d’outils pour format, liens, images.',
    d4: '**Historique** – Voir et restaurer d’anciennes versions.',
    d5: '**Exporter** – Télécharger la page en Markdown.',
    d6: 'Commentaires en bas ; ajoutez et lisez les réponses.',
    tip: '**Ctrl + S** dans l’éditeur pour enregistrer sans quitter.',
  },
  reports: {
    intro:
      'Depuis un projet, ouvrez **Rapports** pour vélocité, flux cumulatif, cartes de contrôle, progression epic, temps, générateur de rapports et vue **Workflow** (Gantt). Exportez données ou graphique pour partager.',
    typesTitle: 'Types de rapports',
    r1: '**Vélocité** – Travail terminé par sprint.',
    r2: '**Flux cumulatif** – Mouvement des tickets entre statuts dans le temps.',
    r3: '**Carte de contrôle** – Temps de cycle des tickets terminés.',
    r4:
      '**Rapport epic** – Liste déroulante **Epic** pour un epic ou tous. Export via le bouton.',
    r5: '**Suivi du temps** – Estimations vs temps passé par type et utilisateur.',
    r6:
      '**Workflow (Gantt)** – Chronologie avec échéances : hiérarchie (epics → tâches → sous-tâches), barres de progression, lignes de dépendance (parent-enfant et liens « bloque »), jalons (même jour en losanges). Zoom (Jour / Semaine / Mois) et **Exporter en image** en PNG. Couleurs selon statuts projet.',
    r7: '**Générateur de rapports** – Grouper par statut, type, priorité ou assigné.',
    tip: 'Vélocité et flux cumulatif aident à voir le débit d’équipe et les goulots.',
  },
  dashboards: {
    body: 'Allez dans **Tableaux de bord personnalisés** pour composer des widgets (par projet, groupe ou assigné). Contrôlez la visibilité par groupe ou rôle.',
  },
  'admin-settings': {
    intro:
      'Ouvrez **Paramètres d’administration** depuis la barre latérale pour l’organisation et la configuration projet.',
    orgTitle: 'Organisation',
    orgBody: 'Nom de l’org, membres et invitations en attente.',
    projCfgTitle: 'Configuration projet',
    projCfgBody:
      'Gérez **Libellés**, **Composants**, **Versions** et **Champs personnalisés** transverses.',
    tplTitle: 'Modèles',
    tplBody:
      'Modèles **projet** et **ticket** pour workflows et champs par défaut.',
    autoTitle: 'Automatisation',
    autoBody:
      'Règles quand les tickets correspondent à des conditions (ex. changement de statut, assignation). Déclencheurs et actions. Icône **Modifier** (crayon) dans le tableau pour nom, description, déclencheur ou état ; **Supprimer** retire la règle.',
    groupsTitle: 'Groupes',
    groupsBody:
      'Créez des groupes, ajoutez des membres, assignez aux projets (Gérer les membres) pour le même rôle projet.',
    rolesTitle: 'Rôles et journal d’audit',
    rolesBody: 'Rôles personnalisés et journal pour les changements importants.',
    systemTitle: 'Système (super admin uniquement)',
    systemBody:
      'L’onglet **Système** liste les organisations. Définissez le **plan** (Free, Pro, Enterprise) et invitez des entreprises.',
  },
  glossary: {
    intro: 'Définitions courtes des termes FlowBoard.',
    g1: '**Ticket** – Élément de travail : tâche, story, bug, epic, sous-tâche.',
    g2: '**Epic** – Grande initiative ; d’autres tickets peuvent lier.',
    g3: '**Sprint** – Lot de tickets sur une période (ex. 2 semaines).',
    g4: '**Backlog** – Tickets pas encore dans un sprint.',
    g5: '**Version** – Livraison ou jalon (ex. v1.0). Dates optionnelles ; peut être marquée Publiée.',
    g6: '**Version de correction** – Version où le ticket doit être corrigé ou livré. Visible sur la feuille de route.',
    g7: '**Version affectée** – Version(s) impactée(s) (ex. bugs). Aussi sur la feuille de route.',
    g8: '**Tableau Kanban** – Colonnes par statut ; glissez les cartes.',
    g9: '**Workflow** – Statuts et transitions (ex. À faire → En cours → Terminé).',
    g10: '**Assigné** – Personne responsable du ticket.',
    g11: '**Observateurs** – Utilisateurs notifiés des mises à jour.',
  },
  'subscription-plans': {
    body: 'Le plan Free limite projets et utilisateurs ; les offres supérieures en autorisent davantage (y compris illimité). À la limite, la création est bloquée jusqu’à mise à niveau. Les super admins règlent le plan dans Paramètres admin → Système.',
  },
  'api-integrations': {
    intro:
      'FlowBoard expose une API REST pour tickets, projets, rapports, webhooks, etc. Utilisez l’URL de base de votre instance avec un jeton valide (ex. après connexion).',
    webhooksTitle: 'Webhooks',
    webhooksBody:
      'Les admins org configurent des webhooks sortants dans **Admin → Webhooks**. POST HTTP sur événements tickets/projets ; secret HMAC optionnel. Pour Slack, ajoutez un webhook avec l’URL Slack (ou règles d’automatisation avec action Webhook).',
    openapiNote:
      'Spécification OpenAPI 3.0 sur **/api/docs/openapi.json** avec le backend actif ; à utiliser avec Swagger ou autres outils.',
  },
};

export default helpSectionsFr;
